import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import FloatingActionButton from '../../components/navigation/FloatingActionButton';
import PostCard from './components/PostCard';
import PostSkeleton from './components/PostSkeleton';
import EmptyState from './components/EmptyState';
import { fetchPosts, addReaction, flagPost, deletePost, getTimeAgo, getUserReactions } from '../../services/postsService';

const MainFeed = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [userReactions, setUserReactions] = useState({});
  const observerTarget = useRef(null);

  const POSTS_PER_PAGE = 6;

  const loadInitialPosts = useCallback(() => {
    setLoading(true);
    fetchPosts(POSTS_PER_PAGE, 0)
      ?.then(async ({ posts: fetchedPosts, total }) => {
        const postsWithTimeAgo = fetchedPosts?.map(post => ({
          ...post,
          timeAgo: getTimeAgo(post?.createdAt),
          isAuthor: false
        }));
        setPosts(postsWithTimeAgo);
        setHasMore(fetchedPosts?.length < total);
        setLoading(false);
        setPage(1);

        // Fetch user reactions for these posts
        if (fetchedPosts?.length > 0) {
          const postIds = fetchedPosts?.map(p => p?.id);
          const reactions = await getUserReactions(postIds);
          setUserReactions(reactions);
        }
      })
      ?.catch((error) => {
        console.error('Failed to load posts:', error);
        setLoading(false);
      });
  }, []);

  const loadMorePosts = useCallback(() => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = page + 1;
    const offset = nextPage * POSTS_PER_PAGE;

    fetchPosts(POSTS_PER_PAGE, offset)
      ?.then(async ({ posts: fetchedPosts, total }) => {
        if (fetchedPosts?.length > 0) {
          const postsWithTimeAgo = fetchedPosts?.map(post => ({
            ...post,
            timeAgo: getTimeAgo(post?.createdAt),
            isAuthor: false
          }));
          setPosts(prev => [...prev, ...postsWithTimeAgo]);
          setPage(nextPage);

          // Fetch user reactions for new posts
          const postIds = fetchedPosts?.map(p => p?.id);
          const reactions = await getUserReactions(postIds);
          setUserReactions(prev => ({ ...prev, ...reactions }));
        }

        const totalLoaded = offset + fetchedPosts?.length;
        if (totalLoaded >= total) {
          setHasMore(false);
        }

        setLoadingMore(false);
      })
      ?.catch((error) => {
        console.error('Failed to load more posts:', error);
        setLoadingMore(false);
      });
  }, [page, loadingMore, hasMore]);

  useEffect(() => {
    loadInitialPosts();
  }, [loadInitialPosts]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget?.current;
    if (currentTarget) {
      observer?.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer?.unobserve(currentTarget);
      }
    };
  }, [hasMore, loadingMore, loadMorePosts]);

  const handleReact = (postId, reactionType) => {
    const previousReaction = userReactions?.[postId];

    // If clicking the same reaction, do nothing
    if (previousReaction === reactionType) {
      return;
    }

    addReaction(postId, reactionType)
      ?.then(() => {
        // Update user reactions state
        setUserReactions(prev => ({
          ...prev,
          [postId]: reactionType
        }));

        // Update post counts optimistically
        setPosts(prevPosts =>
          prevPosts?.map(post =>
            post?.id === postId
              ? {
                  ...post,
                  reactions: {
                    ...post?.reactions,
                    // Decrement previous reaction if exists
                    ...(previousReaction ? {
                      [previousReaction]: Math.max(0, (post?.reactions?.[previousReaction] || 0) - 1)
                    } : {}),
                    // Increment new reaction
                    [reactionType]: (post?.reactions?.[reactionType] || 0) + 1
                  }
                }
              : post
          )
        );
      })
      ?.catch((error) => {
        console.error('Failed to add reaction:', error);
      });
  };

  const handleFlag = (postId) => {
    flagPost(postId)?.then(() => {
        // Hide post immediately in UI
        setPosts(prevPosts =>
          prevPosts?.map(post =>
            post?.id === postId
              ? { ...post, isHidden: true }
              : post
          )
        );
      })?.catch((error) => {
        console.error('Failed to flag post:', error);
      });
  };

  const handleEdit = (postId) => {
    alert('Edit functionality would open an edit modal or navigate to edit screen');
  };

  const handleDelete = (postId) => {
    deletePost(postId)?.then(() => {
        setPosts(prevPosts => prevPosts?.filter(post => post?.id !== postId));
      })?.catch((error) => {
        console.error('Failed to delete post:', error);
      });
  };

  return (
    <>
      <Helmet>
        <title>Main Feed - OnlyCuts</title>
        <meta name="description" content="Browse and support anonymous emotional stories from the OnlyCuts community. Share reactions and connect with others experiencing similar struggles." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 md:px-6 lg:px-8 pt-[76px] md:pt-[76px] pb-24 md:pb-28 lg:pb-32">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 md:mb-8 lg:mb-10">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2 md:mb-3">
                Community Stories
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-muted-foreground">
                Read and support others sharing their emotional journeys
              </p>
            </div>

            {loading ? (
              <div className="space-y-4 md:space-y-5 lg:space-y-6">
                {[1, 2, 3]?.map(i => (
                  <PostSkeleton key={i} />
                ))}
              </div>
            ) : posts?.length === 0 ? (
              <EmptyState />
            ) : (
              <>
                <div className="space-y-4 md:space-y-5 lg:space-y-6">
                  {posts?.map(post => (
                    <PostCard 
                      key={post?.id} 
                      post={post} 
                      onReact={handleReact}
                      onFlag={handleFlag}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      userReaction={userReactions?.[post?.id]}
                    />
                  ))}
                </div>

                {loadingMore && (
                  <div className="mt-4 md:mt-5 lg:mt-6 space-y-4 md:space-y-5 lg:space-y-6">
                    {[1, 2]?.map(i => (
                      <PostSkeleton key={i} />
                    ))}
                  </div>
                )}

                {hasMore && (
                  <div 
                    ref={observerTarget} 
                    className="h-20 flex items-center justify-center mt-4 md:mt-5 lg:mt-6"
                  >
                    <p className="caption text-muted-foreground">
                      Loading more stories...
                    </p>
                  </div>
                )}

                {!hasMore && posts?.length > 0 && (
                  <div className="text-center mt-8 md:mt-10 lg:mt-12 py-6 md:py-8">
                    <p className="text-sm md:text-base text-muted-foreground">
                      You've reached the end of the feed
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        <FloatingActionButton />
      </div>
    </>
  );
};

export default MainFeed;